import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';

// Layouts
import DashboardLayout from './components/layouts/DashboardLayout';
import MarketingLayout from './components/layouts/MarketingLayout';

// Feedback Widget
import { FeedbackProvider } from './components/feedback';

// Marketing Pages
import Home from './pages/marketing/Home';
import Features from './pages/marketing/Features';
import Pricing from './pages/marketing/Pricing';
import About from './pages/marketing/About';
import Contact from './pages/marketing/Contact';
import Help from './pages/marketing/Help';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import Clients from './pages/dashboard/Clients';
import Requests from './pages/dashboard/Requests';
import RequestDetail from './pages/dashboard/RequestDetail';
import CreateRequest from './pages/dashboard/CreateRequest';
import ClientFile from './pages/dashboard/ClientFile';
import Reports from './pages/dashboard/Reports';
import Settings from './pages/dashboard/Settings';
import Companies from './pages/dashboard/Companies';
import CompanyDetail from './pages/dashboard/CompanyDetail';
import AllClients from './pages/dashboard/AllClients';
import Users from './pages/dashboard/Users';
import PlatformSettings from './pages/dashboard/PlatformSettings';
import VerificationSources from './pages/dashboard/VerificationSources';
import VerificationLevels from './pages/dashboard/VerificationLevels';
import API from './pages/dashboard/API';
import PlatformAPITokens from './pages/dashboard/PlatformAPITokens';
import Monitoring from './pages/dashboard/Monitoring';
import DocsViewer from './pages/dashboard/DocsViewer';
import MyVerification from './pages/dashboard/MyVerification';
import MyProfile from './pages/dashboard/MyProfile';
import MyDocuments from './pages/dashboard/MyDocuments';
import Analytics from './pages/dashboard/Analytics';
import AuditLogs from './pages/dashboard/AuditLogs';
import KybVerificationLevels from './pages/dashboard/KybVerificationLevels';
import KybSessions from './pages/dashboard/KybSessions';
import KybSessionDetail from './pages/dashboard/KybSessionDetail';
import KybQuestionFlow from './pages/dashboard/KybQuestionFlow';
import ClientDetail from './pages/dashboard/ClientDetail';

// Components
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  const { initialized } = useAuthStore();

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Marketing Routes */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Help />} />
        </Route>

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Dashboard Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/requests/new" element={<CreateRequest />} />
            <Route path="/requests/:id" element={<RequestDetail />} />
            <Route path="/client-files/:id" element={<ClientFile />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/api" element={<API />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/my-verification" element={<MyVerification />} />
            <Route path="/my-profile" element={<MyProfile />} />
            <Route path="/my-documents" element={<MyDocuments />} />
            {/* Documentation Routes */}
            <Route path="/docs/:docId" element={<DocsViewer />} />
            {/* Platform Admin Routes */}
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:slug" element={<CompanyDetail />} />
            <Route path="/all-clients" element={<AllClients />} />
            <Route path="/all-clients/:id" element={<ClientDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/platform-settings" element={<PlatformSettings />} />
            <Route path="/verification-sources" element={<VerificationSources />} />
            <Route path="/verification-levels" element={<VerificationLevels />} />
            <Route path="/kyb-verification-levels" element={<KybVerificationLevels />} />
            <Route path="/kyb-sessions" element={<KybSessions />} />
            <Route path="/kyb-sessions/:id" element={<KybSessionDetail />} />
            <Route path="/kyb-sessions/:id/continue" element={<KybQuestionFlow />} />
            <Route path="/platform-api-tokens" element={<PlatformAPITokens />} />
            <Route path="/monitoring" element={<Monitoring />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
          </Route>
        </Route>
      </Routes>

      {/* Feedback Widget - Available on all pages */}
      <FeedbackProvider />
    </>
  );
}

export default App;
